"use strict";

import * as AppDispatcher from '../dispatchers/AppDispatcher';
import ActionTypes from './ActionTypes';
import ApiService from '../lib/api/ApiService';
import Config from '../lib/Config';
import {getMigrationPageView, isExternalRedirectUrl, MigrationPageView} from '../lib/migrationUtils';

function parseFailCount(dto) {
    if (!dto) {
        return 0;
    }
    if (typeof dto.FailCount === 'number') {
        return dto.FailCount;
    }
    return parseInt(dto.FailCount, 10) || 0;
}

export default {
    getMigrationStatus: (params = {}) => {
        const promise = ApiService
            .getMigrationStatus(params)
            .then((migrationStatusDTO) => ({migrationStatusDTO}));

        AppDispatcher.dispatchAsync(promise, {
            request: ActionTypes.REQUEST_GET_MIGRATION_STATUS,
            success: ActionTypes.REQUEST_GET_MIGRATION_STATUS_SUCCESS,
            failure: ActionTypes.REQUEST_GET_MIGRATION_STATUS_ERROR
        }, params);
    },

    /**
     * Apply a polled migration status response without toggling page loading UI.
     *
     * @param {Object} migrationStatusDTO
     */
    applyPolledMigrationStatus: (migrationStatusDTO) => {
        AppDispatcher.dispatch(
            ActionTypes.REQUEST_GET_MIGRATION_STATUS_SUCCESS,
            {silent: true},
            {migrationStatusDTO}
        );
    },

    requestMigration: (params = {}) => {
        // Re-check status before submitting so a stale "Migrate" click can't
        // overwrite a PENDING / COMPLETED / multi-FAILED state. If the latest
        // status no longer allows a new request, just surface it so the page
        // swaps to the matching outcome view (in-progress / failed / complete).
        const promise = ApiService
            .getMigrationStatus()
            .then((latestStatusDTO) => {
                const latestStatus = latestStatusDTO && latestStatusDTO.MigrationStatus;
                const latestFailCount = parseFailCount(latestStatusDTO);
                const pageView = getMigrationPageView(latestStatus, latestFailCount);

                if (pageView !== MigrationPageView.FORM) {
                    return {migrationStatusDTO: latestStatusDTO};
                }

                return ApiService
                .requestMigration(params)
                .then((startResponse) => {
                    // Only a real submission carries a hand-off URL, so the
                    // fallback belongs here rather than on the early return
                    // above: a stale click must not redirect anyone. A relative
                    // hand-off URL counts as no URL, since navigating to one
                    // keeps the user on the storefront origin.
                    const handOffUrl = startResponse && startResponse.redirect_url;
                    const url = isExternalRedirectUrl(handOffUrl)
                        ? handOffUrl
                        : Config.RAKUTEN_TV_WEB_URL;
                    // Refresh status so the component re-renders with the latest
                    // state first, then navigates to the redirect_url (if any) after
                    // that update. Never let a failed status refetch swallow a
                    // successful migration's redirect.
                    return ApiService.getMigrationStatus()
                        .then((migrationStatusDTO) => ({migrationStatusDTO, redirectUrl: url}))
                        .catch(() => ({migrationStatusDTO: null, redirectUrl: url}));
                })
            });
        AppDispatcher.dispatchAsync(promise, {
            request: ActionTypes.REQUEST_MIGRATION,
            success: ActionTypes.REQUEST_MIGRATION_SUCCESS,
            failure: ActionTypes.REQUEST_MIGRATION_ERROR
        }, params);
    }
};
